import React from "react";
import { AirlineNote } from "types/airlineNotes";

interface AirlineNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  airlineNote: AirlineNote | null;
}

/**
 * Airline Notes Modal Component
 * Displays airline-specific policies and information
 *
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback to close modal
 * @param airlineNote - Airline notes data to display
 */
const AirlineNotesModal: React.FC<AirlineNotesModalProps> = ({
  isOpen,
  onClose,
  airlineNote,
}) => {
  const formatSectionTitle = React.useCallback((key: string) => {
    return key
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const normalizeContent = React.useCallback(
    (value: string | string[] | null | undefined) => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .map((item) => item?.trim())
          .filter((item): item is string => Boolean(item));
      }

      return value
        .split(/\n+/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    },
    []
  );

  const sectionEntries = React.useMemo(() => {
    if (!airlineNote?.sections)
      return [] as Array<{ key: string; title: string; items: string[] }>;

    return Object.entries(airlineNote.sections)
      .map(([key, value]) => {
        const items = normalizeContent(value);
        return {
          key,
          title: formatSectionTitle(key),
          items,
        };
      })
      .filter((entry) => entry.items.length > 0);
  }, [airlineNote?.sections, formatSectionTitle, normalizeContent]);

  // Handle ESC key - must be before early return
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Early return after hooks
  if (!isOpen || !airlineNote) return null;

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderSection = (title: string, items: string[], key: string) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-6" key={key}>
        <h3 className="text-sm font-bold mb-3 tracking-wide text-grease-100">
          {title}
        </h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li key={index} className="text-grease-100 text-sm flex">
                <span className="mr-2 text-gray-400">•</span>
                {key === "website" ? (
                  <a
                    href={item.startsWith("http") ? item : `https://${item}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    {item}
                  </a>
                ) : (
                  <span>{item}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50 p-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-white rounded-lg shadow pb-4">
          {/* Header */}
          <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
            <p className="text-base font-semibold text-grease-100">
              Airline notes for {airlineNote.airlineName}
            </p>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="px-4 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {sectionEntries.length > 0 ? (
              sectionEntries.map(({ key, title, items }) =>
                renderSection(title, items, key)
              )
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">
                  No airline notes available for {airlineNote.airlineName}
                </p>
              </div>
            )}

            {/* Last Updated (if available) */}
            {(airlineNote.lastUpdated || airlineNote.createdAt) && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-gray-400 text-xs text-center">
                  Last updated{" "}
                  {new Date(
                    airlineNote.lastUpdated || airlineNote.createdAt || ""
                  ).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Footer with Close Button */}
          <div className="flex justify-end gap-3 px-4 py-3 border-t border-lightgray-100">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-[#FFBC00] hover:bg-yellow-500 text-[#F5F9FE] text-xs rounded-lg transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirlineNotesModal;
